/**
 * Store.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { Buhlmann } from '../core/Buhlmann.js'
import { CCR } from '../core/CCR.js'
import { Gas } from '../core/Gas.js'
import { debugStore } from '../utils/debug.js'

// Reactive store for dive planning state with LocalStorage persistence
const STORAGE_KEY = 'mvplan_state'

// Reactive state store with localStorage persistence and subscriber notification
export class Store {
  // Water type written in environment.waterType when nothing was ever chosen.
  // A preset key and not a number: this is the value every state persisted by
  // the previous versions carries, and it must keep producing the same plan.
  static DEFAULT_WATER_TYPE = 'salt'

  // i18n key naming each preset of Gas.WATER_PRESETS. The settings screen and
  // the plan screen build their water selector from this map, so a preset
  // added to the engine has exactly one label to declare here.
  static WATER_PRESET_LABEL_KEYS = {
    fresh: 'settings_water_fresh',
    brackish: 'settings_water_brackish',
    salt: 'settings_water_salt',
    red_sea: 'settings_water_red_sea'
  }

  // Value of the "custom density" entry of the water selectors. It is a screen
  // value only: what reaches environment.waterType is always a preset key or a
  // density in g/l, the two forms Gas.waterDensity accepts.
  static CUSTOM_WATER = 'custom'

  // Preset key matching a stored water type, or CUSTOM_WATER when the
  // environment carries a density typed by the diver (or a value the presets
  // do not describe). Never throws: it drives a selector, not a computation.
  static waterPresetKey(waterType) {
    if (typeof waterType === 'string' &&
        Object.prototype.hasOwnProperty.call(Gas.WATER_PRESETS, waterType)) {
      return waterType
    }
    return Store.CUSTOM_WATER
  }

  // True when `value` is a density the planner accepts, in g/l.
  // Same bounds as Gas.waterDensity, used by the views to refuse a typed value
  // before it reaches the store instead of catching the PlanError afterwards.
  static isUsableDensity(value) {
    return typeof value === 'number' && Number.isFinite(value) &&
      value >= Gas.SALINITY_MIN && value <= Gas.SALINITY_MAX
  }

  // True when `value` is an altitude the planner accepts, in metres.
  // Counterpart of isUsableDensity: the two halves of the environment are
  // typed on the same screens and deserve the same guard. An emptied number
  // input yields NaN, which used to be stored as is and silently planned the
  // dive at sea level - the failure that matters most at a mountain lake.
  static isUsableAltitude(value) {
    return typeof value === 'number' && Number.isFinite(value) &&
      value >= Gas.ALTITUDE_MIN && value <= Gas.ALTITUDE_MAX
  }

  // environment.altitude read back from a persisted state, in metres.
  // Same contract as normalizeWaterType: a value written by any past version
  // has to load, an altitude persisted as a string is converted rather than
  // discarded so the diver keeps his site, and anything unusable falls back to
  // the default instead of throwing on every single calculation.
  // Returns the value to store; never throws.
  static normalizeAltitude(value, fallback = 0) {
    if (value === undefined || value === null) return fallback

    if (typeof value === 'string') {
      const parsed = Number(value)
      if (Store.isUsableAltitude(parsed)) {
        console.warn('Store.normalizeAltitude: altitude stored as the string %o, converted to %o m', value, parsed)
        return parsed
      }
      console.warn('Store.normalizeAltitude: unusable altitude %o, falling back to %o m', value, fallback)
      return fallback
    }

    if (Store.isUsableAltitude(value)) return value

    console.warn('Store.normalizeAltitude: altitude %o outside [%o, %o] m, falling back to %o m',
      value, Gas.ALTITUDE_MIN, Gas.ALTITUDE_MAX, fallback)
    return fallback
  }

  // Density of a stored water type, in g/l, or null when it is unusable.
  // Gas.waterDensity logs the reason and throws; a screen that only needs to
  // display the figure must not be brought down by it, so the exception is
  // turned into a null the caller displays as '--'.
  static waterDensityOrNull(waterType) {
    try {
      return Gas.waterDensity(waterType)
    } catch (e) {
      console.error('Store.waterDensityOrNull: unusable water type %o (%s)', waterType, e.message)
      return null
    }
  }

  // Repair the water type read from a persisted state.
  //
  // environment.waterType has two legal forms (see Gas.waterDensity): a preset
  // key ('fresh', 'brackish', 'salt', 'red_sea') or a density in g/l. Browsers
  // carry states written by every past version of the application, so the
  // historical strings are returned untouched - 'salt' and 'fresh' keep giving
  // exactly the same plan - and anything unusable is brought back to the
  // default rather than left to throw on every single calculation.
  // Returns the value to store; never throws.
  static normalizeWaterType(value, fallback = Store.DEFAULT_WATER_TYPE) {
    if (value === undefined || value === null) {
      debugStore('normalizeWaterType: waterType absent, using %o', fallback)
      return fallback
    }

    if (typeof value === 'string') {
      if (Object.prototype.hasOwnProperty.call(Gas.WATER_PRESETS, value)) return value
      // A density persisted as a string: the engine only accepts preset keys in
      // string form, so it is converted rather than thrown away, which keeps
      // the site the diver had selected.
      const parsed = Number(value)
      if (Store.isUsableDensity(parsed)) {
        console.warn('Store.normalizeWaterType: waterType stored as the string %o, converted to the density %o g/l',
          value, parsed)
        return parsed
      }
      console.warn('Store.normalizeWaterType: unknown water type %o, falling back to %o', value, fallback)
      return fallback
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      if (Store.isUsableDensity(value)) return value
      console.warn('Store.normalizeWaterType: water density %o g/l outside [%o, %o], falling back to %o',
        value, Gas.SALINITY_MIN, Gas.SALINITY_MAX, fallback)
      return fallback
    }

    console.warn('Store.normalizeWaterType: unusable water type %o (%s), falling back to %o',
      value, typeof value, fallback)
    return fallback
  }

  // -------------------------------------------------------------------------
  // Which state a computed plan depends on
  //
  // Invariant: as soon as an input the displayed plan was computed from
  // changes, that plan is dropped. It must never stay readable, exportable and
  // saveable under a header already showing the new values - a diver reading
  // stops computed for other gradient factors, another ascent rate or another
  // machine is the exact failure this whole block exists to prevent.
  //
  // The two lists below are EXHAUSTIVE and EXPLICIT on purpose. Every leaf of
  // the persisted state is named in one of them (tests/planInvalidation.test.js
  // fails on any leaf named in neither), so a setting added tomorrow forces the
  // developer to answer the only question that matters: does the calculation
  // read it? Deducing the answer by exclusion is what let the gradient factors,
  // the ascent rates, the ppO2 limits and the whole CCR block keep a stale plan
  // on screen for as long as they did.
  // -------------------------------------------------------------------------

  // Reason a displayed plan was dropped, written into state.results and read
  // by the screens to pick the sentence shown to the diver.
  static STALE_REASON_ENVIRONMENT = 'environment'
  static STALE_REASON_INPUTS = 'inputs'

  // Classification returned by classifyPath.
  static PLAN_INPUT = 'input'
  static PLAN_NEUTRAL = 'neutral'

  // State the decompression calculation reads. Each entry is quoted with the
  // code that consumes it; the "changes the plan" column of every one of them
  // is checked by execution in tests/planInvalidation.test.js, which recomputes
  // a real plan before and after the change.
  static PLAN_INPUT_PATHS = Object.freeze([
    // PlanView.createPlanner -> new DivePlanner({...})
    'settings.algorithm',
    'settings.ascentRate',
    'settings.descentRate',
    'settings.lastStopDepth',
    'settings.safetyStopTime',
    'settings.lastStopAscentRate',
    'settings.decoAscentRate',
    'settings.ppO2Max',
    'settings.ppO2Deco',
    'settings.gasSwitchAtStop',
    'settings.gasSwitchDuration',
    // PlanView.adjustSegmentsForDescent (with settings.descentRate above)
    'settings.descentTimeIncluded',
    // PlanView.computePlan -> planOC / planCCR
    'settings.gfLow',
    'settings.gfHigh',
    'settings.rmvBottom',
    'settings.rmvDeco',
    // PlanView.computePlan -> planBailout
    'settings.rmvStress',
    'settings.stressUntil',
    'settings.bailDelay',
    // DivePlanner environment: Gas.waterDensity and Gas.pressureAtDepth, so
    // every depth <-> pressure conversion of the dive
    'environment.waterType',
    'environment.altitude',
    // PlanView.buildCCRConfig -> new CCR({...}), and the OC / CCR switch itself
    'ccr.enabled',
    'ccr.mode',
    'ccr.fixedSetpoint',
    'ccr.dynamicSetpoints.descent',
    'ccr.dynamicSetpoints.bottom',
    'ccr.dynamicSetpoints.ascent',
    // Not read by the engine (PlanView.buildCCRConfig resolves the diluent from
    // state.diluents) but printed by PdfExport as the mix of the dive: an
    // exported sheet must not name a diluent the plan was not computed with.
    'ccr.diluent',
    // Deliberate loop flushes, and the switch that decides whether they are
    // applied at all: both change the mix the ascent is breathed on, so both
    // drop the computed plan.
    'ccr.flushes',
    'settings.ccrFlushEnabled',
    // The oxygen flush of the shallow stops, described once in the CCR settings
    // and injected into every rebreather dive by DayPlanner.resolveFlushes. It
    // adds a row to the ascent and leaves the loop on oxygen above it, so it is
    // as much a plan input as a hand written flush.
    'settings.ccrO2FlushEnabled',
    'settings.ccrO2FlushDepth',
    'settings.ccrO2FlushTime',
    // The three collections: the dive itself and the mixes it is breathed on
    'gases',
    'diluents',
    'segments'
  ])

  // State that reaches no calculation. Changing one of these leaves the
  // displayed plan exactly as computed, so dropping it would be a defect of its
  // own: invalidating on every keystroke of the notes field, or every time the
  // diver switches the theme, teaches him to ignore the notice that must warn
  // him when the plan really is obsolete.
  static PLAN_NEUTRAL_PATHS = Object.freeze([
    // Printed on the PDF, never read by the engine
    'settings.diverNames',
    'settings.diveSite',
    'settings.diveNotes',
    // Display of the interface
    'settings.language',
    'settings.units',
    'settings.chartScale',
    // Which alerts are shown. analyzeAlerts runs again at every render from the
    // live state, so these can never leave a stale alert on screen.
    'settings.showNarcosisAlerts',
    'settings.showDensityAlerts',
    'settings.showIcdAlerts',
    // The density column is read off the plan already computed, segment by
    // segment, at render time: showing or hiding it changes no depth and no
    // duration.
    'settings.showGasDensity',
    // Duration proposed when the diver adds a flush. Every flush carries its
    // own time once added, so changing the default never touches an existing
    // plan.
    'settings.ccrFlushDuration',
    // Layout of the exported PDF
    'settings.pdfIncludeProfile',
    // The GF preset list is a set of shortcuts. Only the applied settings.gfLow
    // and settings.gfHigh reach the model.
    'settings.gfPresets',
    // School mode only adds comparison curves next to the plan, computed by
    // PlanView.computeSchoolModeResults at the next calculation. The dive being
    // planned is not touched.
    'settings.schoolMode',
    'settings.schoolModePresets',
    // Lung temperature. It only feeds Gas.gasDensity, used by the breathing gas
    // alerts, which are recomputed at every render: it appears in a plan only
    // as an echo of the environment inside the model snapshot, never in a
    // depth, a duration or a stop (checked in tests/planInvalidation.test.js).
    'environment.gasTemp',
    // The computed plan itself. Invalidating it here is what setResults would
    // undo half a line later, and would loop the store on itself.
    'results',
    // The day of diving. Its dives are planned by the day screen, from their
    // own segments and their own surface intervals: none of them reaches the
    // single dive of the plan screen, so editing the series must not drop the
    // plan displayed there (checked by execution in
    // tests/planInvalidation.test.js, like every other entry of this list).
    //
    // The reverse is not true and is handled where it belongs: the settings,
    // the environment, the gases and the diluents are shared by the whole day,
    // so changing one of them invalidates every dive of the series through
    // invalidateResults({ day: true }).
    'day'
  ])

  // Values that have to be compared through the engine's own reading of them,
  // so that writing another spelling of the same input changes nothing.
  // Prototype-less on purpose: this map is indexed by a path coming from a
  // caller, and 'toString' or 'constructor' must not resolve to an inherited
  // function that would then be run as a normalizer.
  static PLAN_INPUT_NORMALIZERS = Object.freeze(Object.assign(Object.create(null), {
    // 'salt' and the density 10300 g/l are one and the same water: switching
    // the selector to "custom" stores the density already in use and must not
    // drop the plan (Gas.waterDensity accepts both forms).
    'environment.waterType': value => Store.waterDensityOrNull(value ?? Store.DEFAULT_WATER_TYPE),
    // Gas.pressureAtDepth reads `altitude || 0`: 0, undefined and NaN are one
    // and the same site.
    'environment.altitude': value => {
      const metres = Number(value)
      return Number.isFinite(metres) ? (metres || 0) : 0
    }
  }))

  // True when `path` is `prefix` itself or something inside it.
  static pathCovers(prefix, path) {
    return path === prefix || path.startsWith(`${prefix}.`)
  }

  // Is a write at `path` a write to something the plan depends on?
  // A path is an input when it is one of PLAN_INPUT_PATHS, something inside one
  // ('ccr.diluent.fO2'), or something that contains one ('settings', written as
  // a whole block). Anything named nowhere is treated as an input and logged:
  // silently keeping a plan that may no longer describe the dive is the one
  // outcome that is never acceptable here.
  static classifyPath(path) {
    if (typeof path !== 'string' || path === '') {
      console.error('Store.classifyPath: unusable path %o, treated as a plan input', path)
      return Store.PLAN_INPUT
    }
    const isInput = Store.PLAN_INPUT_PATHS.some(
      input => Store.pathCovers(input, path) || Store.pathCovers(path, input)
    )
    if (isInput) return Store.PLAN_INPUT
    if (Store.PLAN_NEUTRAL_PATHS.some(neutral => Store.pathCovers(neutral, path))) {
      return Store.PLAN_NEUTRAL
    }
    console.error('Store.classifyPath: "%s" is declared neither in Store.PLAN_INPUT_PATHS nor in Store.PLAN_NEUTRAL_PATHS. Treated as a plan input (the computed plan is dropped). Declare it in one of the two lists.', path)
    return Store.PLAN_INPUT
  }

  // Comparable form of the value written at `path`. JSON is enough: the state
  // holds only numbers, strings, booleans, arrays and plain objects, since it
  // is persisted to localStorage as JSON.
  static planInputSnapshot(path, value) {
    const normalize = Store.PLAN_INPUT_NORMALIZERS[path]
    const normalized = normalize ? normalize(value) : value
    return JSON.stringify(normalized) ?? 'undefined'
  }

  // Sentence to show for a dropped plan: the environment has its own wording,
  // every other input shares the general one. Kept next to the invalidation
  // rule so the plan screen and the settings screen cannot word the same event
  // differently. Returns null when nothing was dropped.
  static staleNoticeKey(results) {
    if (!results || !results.stale) return null
    return results.staleReason === Store.STALE_REASON_ENVIRONMENT
      ? 'plan_environment_changed'
      : 'plan_inputs_changed'
  }

  constructor() {
    this.state = this.loadState() || this.getDefaultState()
    this.listeners = new Set()
  }

  // Return factory-default state with all settings, gases, diluents, and empty segments
  getDefaultState() {
    return {
      // General settings
      settings: {
        algorithm: Buhlmann.ALGORITHMS.ZHL16C,
        gfLow: 0.30,
        gfHigh: 0.85,
        descentRate: 20,        // m/min
        ascentRate: 9,          // m/min
        lastStopDepth: 3,       // meters
        safetyStopTime: 3,      // minutes at the last stop when no deco is owed, 0 = none
        lastStopAscentRate: 3,  // m/min (ascent rate from last stop to surface)
        decoAscentRate: 3,      // m/min (ascent rate between deco stops)
        rmvBottom: 20,          // L/min (RMV for bottom)
        rmvDeco: 15,            // L/min (RMV for deco/bailout)
        rmvStress: 30,          // L/min (RMV during stress phase of bailout)
        stressUntil: 'first_stop', // 'first_stop', 'gas_switch', 'surface'
        ppO2Max: 1.4,           // Working ppO2
        ppO2Deco: 1.6,          // Deco ppO2
        bailDelay: 2,           // minutes before ascending on bailout
        units: 'metric',        // 'metric' or 'imperial'
        language: 'en',         // 'en', 'fr', 'de', 'es', 'it', 'pl'
        descentTimeIncluded: true, // If true, descent time is included in segment duration
        gfPresets: [
          { name: '30/70', gfLow: 30, gfHigh: 70 },
          { name: '30/85', gfLow: 30, gfHigh: 85 },
          { name: '40/80', gfLow: 40, gfHigh: 80 },
          { name: '50/80', gfLow: 50, gfHigh: 80 },
          { name: '70/70', gfLow: 70, gfHigh: 70 },
          { name: '80/80', gfLow: 80, gfHigh: 80 }
        ],
        diverNames: '',         // Comma-separated diver names for PDF
        diveSite: '',           // Dive site name for PDF
        diveNotes: '',          // Free text notes for PDF
        // Alert visibility options (for experienced divers)
        showNarcosisAlerts: true,   // Show END/narcosis warnings
        showDensityAlerts: true,    // Show gas density/CO2 warnings
        showGasDensity: true,       // Show the gas density column on every plan row
        showIcdAlerts: true,        // Show ICD (inner ear decompression) warnings
        pdfIncludeProfile: true,    // Include dive profile graph in PDF export (single-page A4 if false)
        gasSwitchAtStop: false,     // false = switch ASAP at MOD, true = switch at deco stop only
        gasSwitchDuration: 2,       // minutes spent on each gas switch
        ccrFlushEnabled: false,     // Show the loop flush editor on the plan screen
        ccrFlushDuration: 1,        // Default duration of a loop flush, minutes
        // Oxygen flush of the shallow stops, the standard rebreather gesture:
        // at 6 m the loop is replaced with pure oxygen, which gives 1.62 bar in
        // sea water at sea level, and the diver finishes his decompression on
        // it. Described here once rather than retyped on every dive.
        ccrO2FlushEnabled: false,   // Inject the oxygen flush into every CCR dive
        ccrO2FlushDepth: 6,         // Depth of that flush, meters
        ccrO2FlushTime: 1,          // Duration of that flush, minutes
        schoolMode: false,          // Compare multiple GF presets on profile
        schoolModePresets: [0, 1, 2], // Indices into gfPresets[] to compare
        chartScale: 1              // Multiplier for chart line thickness and font size (1, 1.5, 2, 3)
      },

      // Environment settings
      environment: {
        gasTemp: 35,                            // Gas temperature for density calculation (lung temp ~35°C)
        waterType: Store.DEFAULT_WATER_TYPE,    // Gas.WATER_PRESETS key, or a density in g/l
        altitude: 0                             // Altitude in meters
      },

      // CCR configuration
      ccr: {
        enabled: false,
        mode: CCR.MODES.FIXED,
        fixedSetpoint: 1.3,
        dynamicSetpoints: {
          descent: 0.7,
          bottom: 1.3,
          ascent: 1.0
        },
        diluent: { name: 'Air', fO2: 0.21, fHe: 0, fN2: 0.79 },
        // Deliberate loop flushes on the way up:
        // [{ depth, diluentIndex, time, keepMix }].
        // Not dive segments - a flush inside `segments` would move the TTS
        // reference, which is the end of the last segment, and the sheet would
        // print a time to surface measured from the flush depth.
        flushes: []
      },

      // Available gases (for OC or bailout)
      gases: [
        { id: 1, name: 'TX10/70', fO2: 0.10, fHe: 0.70, ppO2Max: 1.4, enabled: false, isBailout: true, tankVolume: 7, tankPressure: 200 },
        { id: 2, name: 'TX18/48', fO2: 0.18, fHe: 0.48, ppO2Max: 1.4, enabled: false, isBailout: true, tankVolume: 7, tankPressure: 200 },
        { id: 3, name: 'Air', fO2: 0.21, fHe: 0.00, ppO2Max: 1.4, enabled: true, isBailout: true, tankVolume: 7, tankPressure: 200 },
        { id: 4, name: 'EAN32', fO2: 0.32, fHe: 0.00, ppO2Max: 1.4, enabled: false, isBailout: true, tankVolume: 7, tankPressure: 200 },
        { id: 5, name: 'EAN50', fO2: 0.50, fHe: 0.00, ppO2Max: 1.6, enabled: false, isBailout: true, tankVolume: 7, tankPressure: 200 },
        { id: 6, name: 'EAN70', fO2: 0.70, fHe: 0.00, ppO2Max: 1.6, enabled: false, isBailout: true, tankVolume: 7, tankPressure: 200 },
        { id: 7, name: 'O2', fO2: 1.00, fHe: 0.00, ppO2Max: 1.6, enabled: false, isBailout: true, tankVolume: 7, tankPressure: 200 },
      ],

      // Diluent gases for CCR
      diluents: [
        { id: 1, name: 'Air', fO2: 0.21, fHe: 0.00, enabled: true },
        { id: 2, name: 'TX14/30 (normo 70m)', fO2: 0.14, fHe: 0.30, enabled: false },
        { id: 3, name: 'TX12/40 (normo 70m)', fO2: 0.12, fHe: 0.40, enabled: false },
        { id: 4, name: 'TX10/50 (hypo 100m)', fO2: 0.10, fHe: 0.50, enabled: false },
        { id: 5, name: 'TX6/70 (hypo 120m)', fO2: 0.06, fHe: 0.70, enabled: false }
      ],

      // Current dive plan segments
      segments: [
        { depth: 40, time: 20, gasId: 3 }
      ],

      // Day of diving: the series of dives planned one after the other, each
      // starting from the tissues the previous one left (src/core/DayPlanner.js).
      //
      // Declared here and not omitted: getPath walks the state and an absent
      // key would read as undefined at every level below it. Empty by default,
      // so nothing changes for a diver who never opens the day screen.
      //
      // The dives hold their inputs only. Their computed results live in the
      // same dives under `results` and are never persisted, exactly like
      // state.results: a plan reloaded from storage was computed from settings
      // that may have changed since.
      day: {
        dives: [],
        // Index of the dive of the day currently open in the plan screen, or
        // null when that screen holds a dive of its own. It is what tells the
        // plan screen to offer "save into the day" instead of leaving the
        // diver with an edited dive nothing will ever read.
        editingIndex: null
      },

      // Computed results (not persisted)
      results: {
        ccr: null,
        bailout: null
      }
    }
  }

  // Load state from LocalStorage
  loadState() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (!saved) return null

      const parsed = JSON.parse(saved)
      debugStore('loadState RAW from localStorage: gfLow=%o (type=%s), gfHigh=%o (type=%s)',
        parsed.settings?.gfLow, typeof parsed.settings?.gfLow,
        parsed.settings?.gfHigh, typeof parsed.settings?.gfHigh)

      // Merge with defaults to handle new fields
      const defaults = this.getDefaultState()
      const mergedSettings = { ...defaults.settings, ...parsed.settings }
      debugStore('loadState MERGED: gfLow=%o (type=%s), gfHigh=%o (type=%s)',
        mergedSettings.gfLow, typeof mergedSettings.gfLow,
        mergedSettings.gfHigh, typeof mergedSettings.gfHigh)

      // Validate critical numeric settings (fix NaN values and wrong format)
      if (typeof mergedSettings.gfLow !== 'number' || isNaN(mergedSettings.gfLow)) {
        debugStore('loadState: gfLow invalid (%o), resetting to default', mergedSettings.gfLow)
        mergedSettings.gfLow = defaults.settings.gfLow
      } else if (mergedSettings.gfLow > 1) {
        // Convert from percentage to decimal if stored as 30 instead of 0.30
        debugStore('loadState: gfLow in percent (%o), converting to decimal', mergedSettings.gfLow)
        mergedSettings.gfLow = mergedSettings.gfLow / 100
      }
      if (typeof mergedSettings.gfHigh !== 'number' || isNaN(mergedSettings.gfHigh)) {
        debugStore('loadState: gfHigh invalid (%o), resetting to default', mergedSettings.gfHigh)
        mergedSettings.gfHigh = defaults.settings.gfHigh
      } else if (mergedSettings.gfHigh > 1) {
        // Convert from percentage to decimal if stored as 85 instead of 0.85
        debugStore('loadState: gfHigh in percent (%o), converting to decimal', mergedSettings.gfHigh)
        mergedSettings.gfHigh = mergedSettings.gfHigh / 100
      }

      // Validate gfPresets (each preset must have valid gfLow and gfHigh numbers)
      if (Array.isArray(mergedSettings.gfPresets)) {
        const validPresets = mergedSettings.gfPresets.filter(p =>
          p && typeof p.gfLow === 'number' && typeof p.gfHigh === 'number' &&
          !isNaN(p.gfLow) && !isNaN(p.gfHigh)
        )
        if (validPresets.length !== mergedSettings.gfPresets.length) {
          debugStore('loadState: some gfPresets invalid, using defaults')
          mergedSettings.gfPresets = defaults.settings.gfPresets
        }
      } else {
        mergedSettings.gfPresets = defaults.settings.gfPresets
      }

      debugStore('loadState FINAL: gfLow=%o, gfHigh=%o', mergedSettings.gfLow, mergedSettings.gfHigh)

      // Water type of a state written by any past version: 'salt' and 'fresh'
      // are kept as they are, a density out of range is brought back to the
      // default with a log. Left alone, an unusable value would make every
      // single calculation throw and the planner unusable until the diver
      // clears the browser storage.
      const mergedEnvironment = { ...defaults.environment, ...parsed.environment }
      mergedEnvironment.waterType = Store.normalizeWaterType(
        mergedEnvironment.waterType, defaults.environment.waterType
      )
      mergedEnvironment.altitude = Store.normalizeAltitude(
        mergedEnvironment.altitude, defaults.environment.altitude
      )
      debugStore('loadState: waterType=%o (type=%s), altitude=%om',
        mergedEnvironment.waterType, typeof mergedEnvironment.waterType, mergedEnvironment.altitude)

      return {
        settings: mergedSettings,
        environment: mergedEnvironment,
        ccr: { ...defaults.ccr, ...parsed.ccr },
        gases: Store.normalizeGasList(parsed.gases, defaults.gases, 'gases'),
        diluents: Store.normalizeGasList(parsed.diluents, defaults.diluents, 'diluents', { cylinder: false }),
        segments: Store.normalizeSegments(parsed.segments, defaults.segments),
        day: Store.normalizeDay(parsed.day, defaults.day),
        results: defaults.results // Never persist results
      }
    } catch (e) {
      console.warn('Failed to load state from LocalStorage:', e)
      return null
    }
  }

  // Save state to LocalStorage
  saveState() {
    try {
      const toSave = {
        settings: this.state.settings,
        environment: this.state.environment,
        ccr: this.state.ccr,
        gases: this.state.gases,
        diluents: this.state.diluents,
        segments: this.state.segments,
        // The dives of the day, inputs only (see persistableDay)
        day: Store.persistableDay(this.state.day)
        // Don't save results
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave))
    } catch (e) {
      console.warn('Failed to save state to LocalStorage:', e)
    }
  }

  // Register a listener called on every state change; returns unsubscribe function
  subscribe(fn) {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  // Update a nested path in state.
  // The plan computed from the previous value is dropped before the store
  // notifies its subscribers, so a view re-rendering on that notification can
  // never paint the old stops under the new value.
  update(path, value) {
    debugStore('update: %s = %o', path, value)
    if (Store.refuseDayWrite(path, 'update')) return
    const previous = this.get(path)
    const keys = path.split('.')
    let obj = this.state
    for (let i = 0; i < keys.length - 1; i++) {
      obj = obj[keys[i]]
    }
    obj[keys[keys.length - 1]] = value
    this.invalidateResultsForWrite(path, previous, value)
    this.saveState()
    this.notify()
  }

  // Set entire section. Same invalidation rule as update(): a whole block
  // ('settings', 'gases', 'segments'...) contains plan inputs, so writing it
  // drops the plan computed from the previous one.
  set(key, value) {
    debugStore('set: %s', key)
    if (Store.refuseDayWrite(key, 'set')) return
    const previous = this.state[key]
    this.state[key] = value
    this.invalidateResultsForWrite(key, previous, value)
    this.saveState()
    this.notify()
  }

  // Drop the computed plan when a write really changed something it depends on.
  //
  // The two values are compared through Store.planInputSnapshot, so rewriting
  // the same input - the GF slider firing on every pixel of the drag, the water
  // selector switched to "custom" on the density already in use - changes
  // nothing and keeps the plan. `previous` has to be read before the write, and
  // a caller that mutated a collection in place before calling update() cannot
  // be caught here: that is what the dedicated mutations below are for.
  // Returns true when a plan was actually dropped.
  invalidateResultsForWrite(path, previous, value) {
    if (Store.classifyPath(path) === Store.PLAN_NEUTRAL) {
      debugStore('invalidateResultsForWrite: %s reaches no calculation, the plan is kept', path)
      return false
    }
    const before = Store.planInputSnapshot(path, previous)
    const after = Store.planInputSnapshot(path, value)
    if (before === after) {
      debugStore('invalidateResultsForWrite: %s rewritten with the same value, the plan is kept', path)
      return false
    }
    // Only 'ccr.enabled' decides which of the two engines runs; the rest of the
    // block is read by PlanView.buildCCRConfig and by nothing else, so in open
    // circuit it cannot make the displayed plan wrong.
    if (Store.pathCovers('ccr', path) && path !== 'ccr' && path !== 'ccr.enabled' && !this.planReadsCCRBlock()) {
      debugStore('invalidateResultsForWrite: %s is a CCR setting and the dive is planned in open circuit, the plan is kept', path)
      return false
    }
    const reason = Store.pathCovers('environment', path)
      ? Store.STALE_REASON_ENVIRONMENT
      : Store.STALE_REASON_INPUTS

    // The day shares the settings, the environment and the mixes with the plan
    // screen, but not the dive itself: state.segments and state.ccr belong to
    // the single dive of that screen, while every dive of the series carries
    // its own. So a gradient factor invalidates the whole day, a segment does
    // not touch it.
    if (Store.writeReachesDay(path)) {
      this.invalidateDayFrom(0, path)
    }

    return this.invalidateResults(reason, path)
  }

  // Inputs shared by every dive of the day. state.segments and state.ccr are
  // absent on purpose: they are the dive of the plan screen, and each dive of
  // the series holds its own.
  static DAY_SHARED_PATHS = Object.freeze(['settings', 'environment', 'gases', 'diluents'])

  // Refuse a generic write into the day, and say what to call instead.
  //
  // The day is the one part of the state whose edition carries a rule the
  // write cannot know: touching dive 2 invalidates dives 2 and 3. update() and
  // set() would write the value and cascade nothing, leaving stale plans
  // chained to a dive that no longer exists - and 'day' being declared neutral,
  // not even the general invalidation would fire.
  //
  // Returns true when the write was refused.
  static refuseDayWrite(path, caller) {
    if (typeof path !== 'string' || !Store.pathCovers('day', path)) return false
    console.error('Store.%s: "%s" writes into the day, which cascades nothing. Use addDive, updateDive, removeDive, setDiveResults or clearDay instead.',
      caller, path)
    return true
  }

  // Does a write at `path` reach the dives of the day?
  static writeReachesDay(path) {
    return Store.DAY_SHARED_PATHS.some(
      shared => Store.pathCovers(shared, path) || Store.pathCovers(path, shared)
    )
  }

  // True when the CCR block and the diluents reach the calculation, i.e. when
  // the dive is planned on a rebreather (PlanView.computePlan branches on it).
  planReadsCCRBlock() {
    return this.state.ccr?.enabled === true
  }

  // True when at least one dive of the day is planned on a rebreather, so the
  // diluents reach the series. Independent from planReadsCCRBlock: the plan
  // screen can be in open circuit while the day holds a rebreather dive, and
  // changing a diluent then leaves the plan screen alone but invalidates the
  // series.
  dayReadsCCRBlock() {
    const dives = this.state.day?.dives
    if (!Array.isArray(dives)) return false
    return dives.some(dive => dive.ccr?.enabled === true)
  }

  // Drop the computed results of every dive of the day from `index` onwards.
  //
  // The rule of a series: a dive is computed from the tissues the previous one
  // left, so editing dive 2 makes dives 2 and 3 obsolete and leaves dive 1
  // untouched. Recomputing the whole day instead would be correct but would
  // throw away work; keeping dive 3 would show a plan chained to a dive that no
  // longer exists.
  //
  // Returns the number of dives whose results were dropped.
  invalidateDayFrom(index = 0, source = 'the day') {
    const dives = this.state.day?.dives
    if (!Array.isArray(dives)) {
      console.error('Store.invalidateDayFrom: the state carries no dive list, nothing invalidated (day=%o)', this.state.day)
      return 0
    }

    const from = Math.max(0, index)
    let dropped = 0
    for (let i = from; i < dives.length; i++) {
      if (!dives[i].results) continue
      dives[i].results = null
      dives[i].error = null
      // `stale` and not just a null result: a dive that was computed and then
      // invalidated has to be told apart from one never computed, exactly as
      // state.results.stale does for the plan screen. Without it the day screen
      // would have to invent its own wording for a plan it silently dropped.
      dives[i].stale = true
      dropped++
    }

    if (dropped > 0) {
      console.warn('Store: %s changed, %d dive(s) of the day from #%d no longer describe the series and are dropped',
        source, dropped, from + 1)
    } else {
      debugStore('invalidateDayFrom: %s changed, no computed dive from #%d to drop', source, from + 1)
    }
    return dropped
  }

  // Drop the computed plan: it was produced for the dive as it stood before
  // this change, so keeping it would let the screen, the exports and above all
  // SavedPlans.extractSummary pair the new inputs with the old runtime.
  //
  // The marker is written into state.results, the transient slice, and not on a
  // view instance: an input can be changed from the settings or the gases
  // screen while the plan screen is not mounted, and the notice must still be
  // there when the diver comes back to it. The next successful calculation
  // rebuilds the whole slice through setResults, which clears the marker.
  //
  // `reason` is one of the STALE_REASON_* constants and `source` names what
  // changed, for the log. Returns true when a plan was actually dropped.
  invalidateResults(reason = Store.STALE_REASON_INPUTS, source = reason) {
    const results = this.state.results
    const hadPlan = !!(results && (results.ccr || results.bailout))
    const wasStale = !!(results && results.stale)

    if (!hadPlan && !wasStale) {
      debugStore('invalidateResults: %s changed, no plan is displayed, nothing to drop', source)
      return false
    }

    // A second change of another family widens the notice to the general
    // sentence: telling the diver it is only about the water would be false.
    const staleReason = (wasStale && results.staleReason !== reason)
      ? Store.STALE_REASON_INPUTS
      : reason

    if (hadPlan) {
      console.warn('Store: %s changed, the computed plan no longer describes the dive and is dropped', source)
    } else {
      debugStore('invalidateResults: %s changed, the plan was already dropped, notice kept (%s)', source, staleReason)
    }
    this.state.results = { ccr: null, bailout: null, schoolMode: null, stale: true, staleReason }
    return true
  }

  // Call all registered listeners with current state
  notify() {
    this.listeners.forEach(fn => fn(this.state))
  }

  // Read a nested value by dot-separated path (e.g. 'settings.gfLow')
  get(path) {
    return path.split('.').reduce((obj, key) => obj?.[key], this.state)
  }

  // Why a gas literal cannot enter the store, or null when it can.
  //
  // The mix itself goes through Gas.validate, the very check the engine runs
  // at plan time, so a gas the store accepts is a gas the planner accepts. The
  // cylinder fields and the ppO2 limit are optional but, when written, have to
  // be finite and above zero: a 0 L cylinder or a NaN limit used to be stored,
  // persisted, and refused on every calculation with a generic message until
  // the diver found the field again. Gas.validate logs its own reason.
  static gasInputProblem(gas, { cylinder = true } = {}) {
    try {
      Gas.validate(gas)
    } catch (e) {
      return e?.details?.reason || 'invalid_mix'
    }
    const positive = value => value === undefined || value === null || (Number.isFinite(value) && value > 0)
    if (!positive(gas.ppO2Max)) return 'ppO2Max'
    if (cylinder) {
      if (!positive(gas.tankVolume)) return 'tankVolume'
      if (!positive(gas.tankPressure)) return 'tankPressure'
    }
    return null
  }

  // Gas management helpers. addGas returns the new id, or null when the gas
  // was refused; updateGas returns true, or false when refused. Nothing is
  // written on refusal.
  addGas(gas) {
    const problem = Store.gasInputProblem(gas)
    if (problem) {
      console.error('Store.addGas: gas %o refused (%s), nothing added', gas?.name, problem)
      return null
    }
    const maxId = Math.max(0, ...this.state.gases.map(g => g.id))
    const newGas = { ...gas, id: maxId + 1 }
    this.state.gases.push(newGas)
    // An enabled gas immediately joins the list the planner picks from
    // (PlanView.computePlan passes gases.filter(enabled) to planOC / planCCR /
    // planBailout), so it changes the deco schedule of the displayed plan.
    if (newGas.enabled) {
      this.invalidateResults(Store.STALE_REASON_INPUTS, `the gas ${newGas.name}`)
      this.invalidateDayFrom(0, `the gas ${newGas.name}`)
    } else {
      debugStore('addGas: %s added disabled, it reaches no calculation', newGas.name)
    }
    this.saveState()
    this.notify()
    return newGas.id
  }

  updateGas(id, updates) {
    const index = this.state.gases.findIndex(g => g.id === id)
    if (index < 0) {
      console.error('Store.updateGas: no gas with id %o, nothing updated', id)
      return
    }
    const previous = this.state.gases[index]
    const merged = { ...previous, ...updates }
    const problem = Store.gasInputProblem(merged)
    if (problem) {
      console.error('Store.updateGas: gas %o refused (%s), %o kept as it was', merged.name, problem, previous.name)
      return false
    }
    const changed = JSON.stringify(previous) !== JSON.stringify(merged)
    this.state.gases[index] = merged
    if (changed && this.planDependsOnGas(previous, merged)) {
      this.invalidateResults(Store.STALE_REASON_INPUTS, `the gas ${merged.name}`)
      this.invalidateDayFrom(0, `the gas ${merged.name}`)
    } else {
      debugStore('updateGas: %s does not change the plan (changed=%s)', merged.name, changed)
    }
    this.saveState()
    this.notify()
    return true
  }

  removeGas(id) {
    const gas = this.state.gases.find(g => g.id === id)
    if (!gas) {
      console.error('Store.removeGas: no gas with id %o, nothing removed', id)
      return
    }
    this.state.gases = this.state.gases.filter(g => g.id !== id)
    if (this.planDependsOnGas(gas, null)) {
      this.invalidateResults(Store.STALE_REASON_INPUTS, `the gas ${gas.name}`)
      this.invalidateDayFrom(0, `the gas ${gas.name}`)
    } else {
      debugStore('removeGas: %s was neither enabled nor used by a segment', gas.name)
    }
    this.saveState()
    this.notify()
  }

  // Does the displayed plan depend on this gas?
  //
  // The engine only ever breathes the enabled gases: PlanView.computePlan hands
  // gases.filter(enabled) to planOC / planCCR / planBailout, and
  // DivePlanner.planOC picks the bottom gas with Gas.bestGas among them. A gas
  // that is enabled before or after the change therefore moves the plan; one
  // that stays disabled cannot (checked by execution in
  // tests/planInvalidation.test.js).
  //
  // The segment reference is kept in the test as well: DayPlanner.planDive
  // turns segment.gasId into the gasIndex the engine breathes, and refuses a
  // segment whose gas is disabled, so a plan showing that gas must not survive
  // its edition.
  //
  // `next` is the gas after the change, or null when it is being removed.
  planDependsOnGas(gas, next) {
    if (!gas) {
      console.error('Store.planDependsOnGas: no gas given, the plan is dropped by precaution')
      return true
    }
    if (gas.enabled || next?.enabled) return true
    return this.isGasUsedInSegments(gas.id)
  }

  // Update a diluent by id. Diluents only reach the engine on a rebreather
  // (PlanView.buildCCRConfig), so in open circuit the displayed plan stays
  // valid.
  updateDiluent(id, updates) {
    const index = this.state.diluents.findIndex(d => d.id === id)
    if (index < 0) {
      console.error('Store.updateDiluent: no diluent with id %o, nothing updated', id)
      return
    }
    const previous = this.state.diluents[index]
    const merged = { ...previous, ...updates }
    const problem = Store.gasInputProblem(merged, { cylinder: false })
    if (problem) {
      console.error('Store.updateDiluent: diluent %o refused (%s), %o kept as it was', merged.name, problem, previous.name)
      return false
    }
    const changed = JSON.stringify(previous) !== JSON.stringify(merged)
    this.state.diluents[index] = merged
    if (changed && this.planReadsCCRBlock()) {
      this.invalidateResults(Store.STALE_REASON_INPUTS, `the diluent ${merged.name}`)
    } else {
      debugStore('updateDiluent: %s does not change the plan (changed=%s, ccr=%s)',
        merged.name, changed, this.planReadsCCRBlock())
    }
    // The day has its own condition: one rebreather dive in the series is
    // enough, even when the plan screen is set to open circuit
    if (changed && this.dayReadsCCRBlock()) {
      this.invalidateDayFrom(0, `the diluent ${merged.name}`)
    }
    this.saveState()
    this.notify()
    return true
  }

  // Remove a diluent by id. Refuses to empty the list: the CCR loop has to be
  // filled with something, and PlanView.buildCCRConfig falls back to
  // diluents[0] when no diluent is enabled.
  // Returns true when the diluent was removed.
  removeDiluent(id) {
    const index = this.state.diluents.findIndex(d => d.id === id)
    if (index < 0) {
      console.error('Store.removeDiluent: no diluent with id %o, nothing removed', id)
      return false
    }
    if (this.state.diluents.length <= 1) {
      console.error('Store.removeDiluent: refusing to remove the last diluent (id %o)', id)
      return false
    }
    const [removed] = this.state.diluents.splice(index, 1)
    // Segments address their diluent by index into this very array, so a
    // removal also shifts the mix breathed by every segment placed after it.
    if (this.planReadsCCRBlock()) {
      this.invalidateResults(Store.STALE_REASON_INPUTS, `the diluent ${removed.name}`)
    } else {
      debugStore('removeDiluent: %s removed, the dive is planned in open circuit', removed.name)
    }
    // Same for the day: one rebreather dive in the series is enough
    if (this.dayReadsCCRBlock()) {
      this.invalidateDayFrom(0, `the diluent ${removed.name}`)
    }
    this.saveState()
    this.notify()
    return true
  }

  toggleGas(id) {
    const gas = this.state.gases.find(g => g.id === id)
    if (!gas) {
      console.error('Store.toggleGas: no gas with id %o, nothing toggled', id)
      return
    }
    gas.enabled = !gas.enabled
    // Enabling or disabling a gas adds it to or removes it from the list the
    // planner picks its bottom and deco mixes from: it always moves the plan.
    this.invalidateResults(Store.STALE_REASON_INPUTS, `the gas ${gas.name}`)
    this.invalidateDayFrom(0, `the gas ${gas.name}`)
    this.saveState()
    this.notify()
  }

  // Diluent management. Returns the new id, or null when the mix is refused.
  addDiluent(diluent) {
    const problem = Store.gasInputProblem(diluent, { cylinder: false })
    if (problem) {
      console.error('Store.addDiluent: diluent %o refused (%s), nothing added', diluent?.name, problem)
      return null
    }
    const maxId = Math.max(0, ...this.state.diluents.map(d => d.id))
    // Forced disabled: a new diluent is offered to the diver, it does not get
    // into the loop by itself, so no computed plan depends on it yet.
    const newDiluent = { ...diluent, id: maxId + 1, enabled: false }
    this.state.diluents.push(newDiluent)
    debugStore('addDiluent: %s added disabled, it reaches no calculation', newDiluent.name)
    this.saveState()
    this.notify()
    return newDiluent.id
  }

  // Toggle diluent enabled state (for multi-diluent selection)
  toggleDiluent(id) {
    const diluent = this.state.diluents.find(d => d.id === id)
    if (!diluent) {
      console.error('Store.toggleDiluent: no diluent with id %o, nothing toggled', id)
      return
    }
    // Ensure at least one diluent stays enabled
    const enabledCount = this.state.diluents.filter(d => d.enabled).length
    if (diluent.enabled && enabledCount <= 1) {
      console.warn('Store.toggleDiluent: %s is the last enabled diluent, kept enabled', diluent.name)
      return // Don't disable the last enabled diluent
    }
    diluent.enabled = !diluent.enabled
    // The first enabled diluent is the one every segment without an explicit
    // diluentIndex breathes (PlanView.buildCCRConfig), so on a rebreather this
    // changes the loop mix of the dive.
    if (this.planReadsCCRBlock()) {
      this.invalidateResults(Store.STALE_REASON_INPUTS, `the diluent ${diluent.name}`)
    } else {
      debugStore('toggleDiluent: %s toggled, the dive is planned in open circuit', diluent.name)
    }
    // Same for the day: one rebreather dive in the series is enough
    if (this.dayReadsCCRBlock()) {
      this.invalidateDayFrom(0, `the diluent ${diluent.name}`)
    }
    this.saveState()
    this.notify()
  }

  // Legacy: set single active diluent (kept for backward compatibility)
  setActiveDiluent(id) {
    const active = this.state.diluents.find(d => d.id === id)
    if (!active) {
      console.error('Store.setActiveDiluent: no diluent with id %o, selection unchanged', id)
      return
    }
    this.state.diluents.forEach(d => {
      d.enabled = d.id === id
    })
    // Update CCR diluent reference
    this.state.ccr.diluent = {
      name: active.name,
      fO2: active.fO2,
      fHe: active.fHe,
      fN2: 1 - active.fO2 - active.fHe
    }
    if (this.planReadsCCRBlock()) {
      this.invalidateResults(Store.STALE_REASON_INPUTS, `the diluent ${active.name}`)
    } else {
      debugStore('setActiveDiluent: %s selected, the dive is planned in open circuit', active.name)
    }
    // Same for the day: one rebreather dive in the series is enough
    if (this.dayReadsCCRBlock()) {
      this.invalidateDayFrom(0, `the diluent ${active.name}`)
    }
    this.saveState()
    this.notify()
  }

  // Get all enabled diluents for multi-diluent planning
  getEnabledDiluents() {
    return this.state.diluents.filter(d => d.enabled)
  }

  // True if any segment references the given gas id
  isGasUsedInSegments(gasId) {
    return this.state.segments.some(s => s.gasId === gasId)
  }

  // Segment management. The segments are the dive itself: any change to them
  // drops the computed plan.
  addSegment(segment) {
    this.state.segments.push(segment)
    this.invalidateResults(Store.STALE_REASON_INPUTS, 'the dive segments')
    this.saveState()
    this.notify()
  }

  updateSegment(index, updates) {
    if (index < 0 || index >= this.state.segments.length) {
      console.error('Store.updateSegment: index %o outside the %d segments, nothing updated',
        index, this.state.segments.length)
      return
    }
    const merged = { ...this.state.segments[index], ...updates }
    // Remove properties explicitly set to undefined
    Object.keys(merged).forEach(key => {
      if (merged[key] === undefined) delete merged[key]
    })
    this.state.segments[index] = merged
    this.invalidateResults(Store.STALE_REASON_INPUTS, 'the dive segments')
    this.saveState()
    this.notify()
  }

  removeSegment(index) {
    if (index < 0 || index >= this.state.segments.length) {
      console.error('Store.removeSegment: index %o outside the %d segments, nothing removed',
        index, this.state.segments.length)
      return
    }
    this.state.segments.splice(index, 1)
    this.invalidateResults(Store.STALE_REASON_INPUTS, 'the dive segments')
    this.saveState()
    this.notify()
  }

  clearSegments() {
    this.state.segments = []
    this.invalidateResults(Store.STALE_REASON_INPUTS, 'the dive segments')
    this.saveState()
    this.notify()
  }

  // ---------------------------------------------------------------------------
  // Day of diving
  //
  // A dive of the series carries its own segments, its own rebreather
  // configuration and the surface interval that precedes it. The gases, the
  // diluents, the settings and the environment are shared by the whole day.
  //
  // Every mutation invalidates the computed results from the edited dive to the
  // end of the series (invalidateDayFrom): a dive is chained to the tissues the
  // previous one left, so what comes before an edit stays valid and what comes
  // after does not.
  // ---------------------------------------------------------------------------

  // A dive of the day as it is persisted, without its computed results.
  static persistableDive(dive) {
    return {
      id: dive.id,
      label: dive.label || '',
      startTime: dive.startTime || '',
      surfaceIntervalBefore: dive.surfaceIntervalBefore ?? null,
      segments: dive.segments,
      ccr: dive.ccr || null
    }
  }

  // The day as it is persisted: the inputs of every dive, never their results.
  // Same rule as state.results - a plan read back from storage was computed
  // from settings that may have changed since, and nothing would say so.
  static persistableDay(day) {
    const dives = Array.isArray(day?.dives) ? day.dives : []
    return {
      dives: dives.map(Store.persistableDive),
      // Kept on purpose: a diver who closes the application while editing dive
      // 2 finds it still attached to dive 2 when he comes back
      editingIndex: day?.editingIndex ?? null
    }
  }

  // Repair the day read from a persisted state.
  //
  // Same contract as normalizeWaterType and normalizeAltitude: never throws,
  // logs what it drops, and returns something the application can run on.
  //
  // A day is a chain, so an unusable dive TRUNCATES it: that dive and every
  // dive after it are dropped, the ones before it are kept. Skipping the bad
  // dive and keeping the rest was tried and is worse than useless - the dive
  // that followed it becomes the first dive of the day, loses its surface
  // interval and is replanned on clean tissues, which is a repetitive dive
  // demoted to a first dive and a plan shorter than the diver owes. Dropping
  // the tail costs a recomputation; keeping it would cost decompression.
  // Gas or diluent list read from storage. Same contract as normalizeDay:
  // every unusable entry is logged and dropped, never repaired, and the list
  // falls back to the defaults when nothing usable is left - the planner has
  // to have a mix to breathe. A stored list used to be taken as it was
  // (`parsed.gases || defaults.gases`): a gas without an id, or one with a
  // NaN fraction written by an older form, reached the screen and the engine
  // and broke whichever came first.
  static normalizeGasList(value, fallback, label, { cylinder = true } = {}) {
    if (value === undefined || value === null) return fallback
    if (!Array.isArray(value)) {
      console.warn('Store.normalizeGasList: %s is not a list (%o), falling back to the defaults', label, value)
      return fallback
    }
    const kept = []
    const ids = new Set()
    value.forEach((gas, index) => {
      if (!gas || typeof gas !== 'object' || Array.isArray(gas)) {
        console.warn('Store.normalizeGasList: %s[%d] is not a gas (%o), dropped', label, index, gas)
        return
      }
      if (!Number.isFinite(gas.id)) {
        console.warn('Store.normalizeGasList: %s[%d] %o has no usable id (%o), dropped', label, index, gas.name, gas.id)
        return
      }
      if (ids.has(gas.id)) {
        console.warn('Store.normalizeGasList: %s[%d] %o repeats id %o, dropped', label, index, gas.name, gas.id)
        return
      }
      const problem = Store.gasInputProblem(gas, { cylinder })
      if (problem) {
        console.warn('Store.normalizeGasList: %s[%d] %o is unusable (%s), dropped', label, index, gas.name, problem)
        return
      }
      ids.add(gas.id)
      kept.push(gas)
    })
    if (kept.length === 0) {
      console.warn('Store.normalizeGasList: no usable %s in storage, falling back to the defaults', label)
      return fallback
    }
    return kept
  }

  // Segments of the plan screen read from storage: same treatment, with the
  // same test the engine applies to a segment (finite depth and time, both
  // >= 0). A segment with a NaN time used to be loaded and refused at every
  // calculation until the diver edited it.
  static normalizeSegments(value, fallback) {
    if (value === undefined || value === null) return fallback
    if (!Array.isArray(value)) {
      console.warn('Store.normalizeSegments: segments is not a list (%o), falling back to the defaults', value)
      return fallback
    }
    const kept = value.filter((segment, index) => {
      const usable = segment && typeof segment === 'object' && !Array.isArray(segment)
        && Number.isFinite(segment.depth) && segment.depth >= 0
        && Number.isFinite(segment.time) && segment.time >= 0
      if (!usable) console.warn('Store.normalizeSegments: segment #%d is unusable (%o), dropped', index + 1, segment)
      return usable
    })
    if (kept.length === 0) {
      console.warn('Store.normalizeSegments: no usable segment in storage, falling back to the defaults')
      return fallback
    }
    return kept
  }

  static normalizeDay(value, fallback = { dives: [], editingIndex: null }) {
    if (value === undefined || value === null) return fallback

    if (typeof value !== 'object' || !Array.isArray(value.dives)) {
      console.warn('Store.normalizeDay: unusable day %o, falling back to an empty day', value)
      return fallback
    }

    const dives = []
    const truncate = (index, reason, detail) => {
      console.warn('Store.normalizeDay: dive #%d %s (%o), it and the %d dive(s) after it are dropped: a series cannot skip one of its dives',
        index + 1, reason, detail, value.dives.length - index - 1)
    }

    for (let index = 0; index < value.dives.length; index++) {
      const dive = value.dives[index]

      if (!dive || typeof dive !== 'object') {
        truncate(index, 'is not an object', dive)
        break
      }
      if (!Array.isArray(dive.segments) || dive.segments.length === 0) {
        truncate(index, 'carries no segment', dive.segments)
        break
      }
      const usableSegments = dive.segments.every(
        s => s && Number.isFinite(s.depth) && s.depth >= 0 && Number.isFinite(s.time) && s.time >= 0
      )
      if (!usableSegments) {
        truncate(index, 'has an unusable segment', dive.segments)
        break
      }

      // The first dive of the day follows nothing; every other one needs a
      // surface interval, which is what makes it a repetitive dive.
      let surfaceIntervalBefore = null
      if (index > 0) {
        const interval = Number(dive.surfaceIntervalBefore)
        if (!Number.isFinite(interval) || interval < 0) {
          truncate(index, 'has an unusable surface interval', dive.surfaceIntervalBefore)
          break
        }
        surfaceIntervalBefore = interval
      }

      dives.push({
        id: Number.isFinite(dive.id) ? dive.id : dives.length + 1,
        label: typeof dive.label === 'string' ? dive.label : '',
        startTime: typeof dive.startTime === 'string' ? dive.startTime : '',
        surfaceIntervalBefore,
        segments: dive.segments,
        ccr: dive.ccr && typeof dive.ccr === 'object' ? dive.ccr : null,
        // Never restored from storage: it would pair a plan with settings that
        // may have changed since it was computed
        results: null,
        error: null,
        stale: false
      })
    }

    // The plan screen cannot be editing a dive that no longer exists: an index
    // left dangling by the truncation above would save the dive of the plan
    // screen over another one, or over nothing at all.
    let editingIndex = null
    if (Number.isInteger(value.editingIndex) && value.editingIndex >= 0 && value.editingIndex < dives.length) {
      editingIndex = value.editingIndex
    } else if (value.editingIndex !== null && value.editingIndex !== undefined) {
      console.warn('Store.normalizeDay: the stored day was editing dive #%o, which the restored day does not carry, the plan screen is detached from it',
        value.editingIndex)
    }

    debugStore('normalizeDay: %d dive(s) restored out of %d', dives.length, value.dives.length)
    return { dives, editingIndex }
  }

  // True when `value` is a surface interval the series accepts, in minutes.
  //
  // A number, and nothing else. Number() would take '' , null, [] for 0 and
  // true for 1, so an emptied field of the day screen - the very case this
  // guard exists for - would be written as an immediate re-entry, silently
  // turning a two hour interval into no interval at all. The screens convert
  // their text input before writing, exactly as they do for the altitude
  // (Store.isUsableAltitude).
  static isUsableSurfaceInterval(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0
  }

  // Deep copy of the inputs of a dive.
  //
  // The caller keeps its own object - a view builds one from its form fields,
  // a test reuses a literal - and mutating it afterwards would change the day
  // behind the store, with no cascade at all: the invalidation only fires from
  // the mutations, and they can only compare what they are given.
  static copyDiveInputs(dive) {
    return JSON.parse(JSON.stringify({ segments: dive.segments, ccr: dive.ccr || null }))
  }

  // Add a dive at the end of the day. `surfaceIntervalBefore` is required
  // except on the first one, which follows nothing.
  // Returns the id of the dive added, or null when it was refused.
  addDive(dive) {
    if (!dive || !Array.isArray(dive.segments) || dive.segments.length === 0) {
      console.error('Store.addDive: a dive needs at least one segment (dive=%o), nothing added', dive)
      return null
    }

    const dives = this.state.day.dives
    const first = dives.length === 0
    let surfaceIntervalBefore = null

    if (!first) {
      if (!Store.isUsableSurfaceInterval(dive.surfaceIntervalBefore)) {
        console.error('Store.addDive: dive #%d needs a surface interval given as a number of minutes >= 0 (%o, %s), nothing added',
          dives.length + 1, dive.surfaceIntervalBefore, typeof dive.surfaceIntervalBefore)
        return null
      }
      surfaceIntervalBefore = dive.surfaceIntervalBefore
    } else if (dive.surfaceIntervalBefore !== undefined && dive.surfaceIntervalBefore !== null) {
      console.warn('Store.addDive: the first dive of the day follows nothing, the %o min interval given is ignored',
        dive.surfaceIntervalBefore)
    }

    const id = Math.max(0, ...dives.map(d => d.id || 0)) + 1
    const inputs = Store.copyDiveInputs(dive)
    dives.push({
      id,
      label: dive.label || '',
      startTime: dive.startTime || '',
      surfaceIntervalBefore,
      segments: inputs.segments,
      ccr: inputs.ccr,
      results: null,
      error: null,
      stale: false
    })

    debugStore('addDive: dive #%d added (id=%o, %d segments)', dives.length, id, dive.segments.length)
    this.saveState()
    this.notify()
    return id
  }

  // Update a dive of the day. Everything after it - itself included - loses its
  // computed results.
  // Returns true when the dive was really updated: every refusal below returns
  // false, and a caller that reported a save regardless would tell the diver
  // his work was kept when it was dropped.
  updateDive(index, updates) {
    const dives = this.state.day.dives
    if (index < 0 || index >= dives.length) {
      console.error('Store.updateDive: index %o outside the %d dives of the day, nothing updated', index, dives.length)
      return false
    }

    if (updates.surfaceIntervalBefore !== undefined) {
      if (index === 0) {
        // null is the value the first dive legitimately carries, so an update
        // built from the dive itself ({ ...dive, label }) goes through. Only an
        // attempt to give the first dive a real interval is refused.
        if (updates.surfaceIntervalBefore !== null) {
          console.error('Store.updateDive: the first dive of the day follows nothing, its surface interval (%o) is refused',
            updates.surfaceIntervalBefore)
          return false
        }
      } else if (!Store.isUsableSurfaceInterval(updates.surfaceIntervalBefore)) {
        console.error('Store.updateDive: dive #%d needs a surface interval given as a number of minutes >= 0 (%o, %s), nothing updated',
          index + 1, updates.surfaceIntervalBefore, typeof updates.surfaceIntervalBefore)
        return false
      }
    }

    if (updates.segments !== undefined && (!Array.isArray(updates.segments) || updates.segments.length === 0)) {
      console.error('Store.updateDive: dive #%d needs at least one segment (%o), nothing updated',
        index + 1, updates.segments)
      return false
    }

    const previous = dives[index]
    const merged = { ...previous, ...updates }
    // Same deep copy as addDive: what the caller keeps must not be able to
    // change the day behind the store
    const inputs = Store.copyDiveInputs(merged)
    merged.segments = inputs.segments
    merged.ccr = inputs.ccr
    const inputsChanged = JSON.stringify(Store.persistableDive(previous)) !==
      JSON.stringify(Store.persistableDive(merged))
    dives[index] = merged

    if (inputsChanged) {
      // The refusal was about the dive as it stood: editing it makes that
      // verdict obsolete, exactly like its computed plan
      merged.error = null
      this.invalidateDayFrom(index, `dive #${index + 1} of the day`)
    } else {
      debugStore('updateDive: dive #%d rewritten with the same inputs, the series is kept', index + 1)
    }
    this.saveState()
    this.notify()
    return true
  }

  // Remove a dive from the day. The dives after it change predecessor, so they
  // lose their results; and a series that loses its first dive has a new first
  // dive, which follows nothing.
  removeDive(index) {
    const dives = this.state.day.dives
    if (index < 0 || index >= dives.length) {
      console.error('Store.removeDive: index %o outside the %d dives of the day, nothing removed', index, dives.length)
      return
    }

    dives.splice(index, 1)

    if (index === 0 && dives.length > 0 && dives[0].surfaceIntervalBefore !== null) {
      console.warn('Store.removeDive: dive #1 removed, the %o min interval of the new first dive no longer follows anything and is dropped',
        dives[0].surfaceIntervalBefore)
      dives[0].surfaceIntervalBefore = null
    }

    const editing = this.state.day.editingIndex
    if (editing !== null && editing !== undefined) {
      if (editing === index) {
        console.warn('Store.removeDive: the plan screen was editing dive #%d, which is being removed, it is detached from the day', index + 1)
        this.state.day.editingIndex = null
      } else if (editing > index) {
        // Everything after the removed dive shifted by one
        this.state.day.editingIndex = editing - 1
      }
    }

    this.invalidateDayFrom(index, `the removal of dive #${index + 1} of the day`)
    this.saveState()
    this.notify()
  }

  // Store the computed results of one dive of the day.
  // Not persisted, exactly like state.results (see persistableDay).
  // `error` is the plan error code the engine refused this dive with, or null
  // when it planned. It belongs to the store like the results themselves: left
  // to the screen to write, nothing would ever clear it, and a dive corrected
  // by the diver would keep saying it was refused.
  setDiveResults(index, results, error = null) {
    const dives = this.state.day.dives
    if (index < 0 || index >= dives.length) {
      console.error('Store.setDiveResults: index %o outside the %d dives of the day, results dropped', index, dives.length)
      return
    }
    dives[index].results = results
    dives[index].error = error
    dives[index].stale = false
    debugStore('setDiveResults: dive #%d %s', index + 1, results ? 'computed' : 'cleared')
    this.notify()
  }

  // Empty the day. The single dive of the plan screen is untouched.
  clearDay() {
    const count = this.state.day.dives.length
    this.state.day = { dives: [], editingIndex: null }
    debugStore('clearDay: %d dive(s) removed', count)
    this.saveState()
    this.notify()
  }

  // Open a dive of the day in the plan screen.
  //
  // Its segments and its rebreather configuration become the dive of that
  // screen - a copy, so editing there changes nothing until it is saved back -
  // and the index is remembered so the screen knows where to save it.
  // Returns true when the dive was opened.
  startEditingDive(index) {
    const dives = this.state.day.dives
    if (index < 0 || index >= dives.length) {
      console.error('Store.startEditingDive: index %o outside the %d dives of the day, nothing opened', index, dives.length)
      return false
    }

    const dive = dives[index]
    const inputs = Store.copyDiveInputs(dive)
    this.state.segments = inputs.segments
    // A dive of the day with no rebreather configuration is an open circuit
    // dive: the plan screen switches to open circuit rather than keeping the
    // machine of whatever it was showing before.
    this.state.ccr = inputs.ccr
      ? { ...this.state.ccr, ...inputs.ccr }
      : { ...this.state.ccr, enabled: false }
    this.state.day.editingIndex = index

    // The plan on that screen was computed for another dive
    this.invalidateResults(Store.STALE_REASON_INPUTS, `dive #${index + 1} of the day`)
    debugStore('startEditingDive: dive #%d opened in the plan screen', index + 1)
    this.saveState()
    this.notify()
    return true
  }

  // Save the dive of the plan screen back into the day it came from.
  // Returns true when it was saved.
  saveEditedDive() {
    const index = this.state.day.editingIndex
    if (index === null || index === undefined) {
      console.error('Store.saveEditedDive: the plan screen is not editing a dive of the day, nothing saved')
      return false
    }

    // updateDive carries the whole rule: deep copy of the inputs and cascade
    // from this dive to the end of the series. It refuses an unusable dive and
    // says so, and the editing link is then kept: dropping it would leave the
    // diver on a screen that no longer belongs to the day, after a save that
    // did not happen.
    const saved = this.updateDive(index, {
      segments: this.state.segments,
      ccr: this.state.ccr?.enabled ? this.state.ccr : null
    })
    if (!saved) {
      console.error('Store.saveEditedDive: dive #%d was refused, the plan screen stays attached to it', index + 1)
      return false
    }

    this.state.day.editingIndex = null
    debugStore('saveEditedDive: dive #%d saved back into the day', index + 1)
    this.saveState()
    this.notify()
    return true
  }

  // Leave the dive of the day alone: the plan screen keeps what it shows, the
  // day keeps what it had.
  stopEditingDive() {
    this.state.day.editingIndex = null
    debugStore('stopEditingDive: the plan screen no longer edits a dive of the day')
    this.saveState()
    this.notify()
  }

  // Get enabled gases for planning
  getEnabledGases() {
    return this.state.gases.filter(g => g.enabled)
  }

  // Get bailout gases
  getBailoutGases() {
    return this.state.gases.filter(g => g.enabled && g.isBailout)
  }

  // Get active diluent
  getActiveDiluent() {
    return this.state.diluents.find(d => d.enabled) || this.state.diluents[0]
  }

  // Store results (not persisted)
  setResults(ccr, bailout, schoolMode) {
    debugStore('setResults: ccr=%s, bailout=%s, schoolMode=%s',
      ccr ? `${ccr.maxDepth}m/${ccr.runtime}min` : 'null',
      bailout ? `TTS=${bailout.tts}min` : 'null',
      schoolMode ? `${schoolMode.length} presets` : 'null')
    this.state.results = { ccr, bailout, schoolMode: schoolMode || null }
    this.notify()
  }

  // Reset to defaults
  reset() {
    debugStore('reset to defaults')
    this.state = this.getDefaultState()
    this.saveState()
    this.notify()
  }
}
